import { Component, OnInit, Input } from '@angular/core';
import { Machine } from '../masterdata/models/machine';
import { MessagesService } from '../messages/messages.service';
import { CarrierStatusService } from '../carrier-status/carrier-status.service';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { SelectableLab } from '../carrier/selectable-lab';
import { ErrorHandler } from '../utils/error-handler';
import { CarrierSearchService } from '../carrier-search/carrier-search.service';
import { CarrierState } from '../masterdata/models/carrierState';
import { Printer } from '../masterdata/models/printer';
import { ContainerSplitService } from './step-split-containers.service';
import { SplitRequest } from './split-request';
import { ProcessState } from '../masterdata/models/processState-enum';

@Component({
  selector: 'app-step-split-containers',
  templateUrl: './step-split-containers.component.html',
  styleUrls: ['./step-split-containers.component.css']
})
export class StepSplitContainersComponent implements OnInit {

  stepSplitForm: FormGroup;

  machineList: Machine[];
  machineBarcode: string;
  readonly requiredMachineType = "Hamilton";
  splitDoneBy: string;

  containerBarcode: string;
  selectedLab: SelectableLab;
  isReadyForSubmit: boolean;
  isInputDataValid: boolean;

  blocked: boolean

  @Input() carrierStateDetails: CarrierState;
  @Input() printersList: Printer[];
  selectedPrinter: Printer;
  splitRequest: SplitRequest;
  outputBarcodeList: string[];
  barcodeA1: string;
  barcodeA2: string;
  barcodeB1: string;
  barcodeB2: string;

  errorHandler: ErrorHandler = new ErrorHandler;

  constructor(private formBuilder: FormBuilder, private _messagesService: MessagesService,
    private carrierStatusService: CarrierStatusService, private _carrierSearch: CarrierSearchService,
    private containerSplitService: ContainerSplitService) {
    this.selectedLab = JSON.parse(localStorage.getItem('lab'));
  }

  ngOnInit() {

    if(this.carrierStateDetails.printer && this.carrierStateDetails.printer.active)
    {
      this.selectedPrinter = this.carrierStateDetails.printer;
    }
    this.carrierStatusService.getMachineList().subscribe(
      results => {
        this.machineList = results.data;
      })

    this.stepSplitForm = this.formBuilder.group({
      'A1': new FormControl(''),
      'A2': new FormControl(''),
      'B1': new FormControl(''),
      'B2': new FormControl(''),
      'splitDoneBy': new FormControl('', Validators.required),
      'machineBarcode': new FormControl('', Validators.required),
      'targetContainer': new FormControl('', Validators.required),
      'splitSelectedPrinter': new FormControl('', Validators.required)
    });
  }

  /**
   * @function
   * @description: This function is to generate and print barcodes.
   */
  generateBarcodes() {
    
    this.blocked = true
    this.isReadyForSubmit = false;
    this.resetReadOnlyFields();
    this.splitRequest = {lab: this.selectedLab.lab, createdBy: this.splitDoneBy, machineBarcode: this.machineBarcode.trim(),
      inputBarcode: this.containerBarcode.trim(), targetBarcodes: this.outputBarcodeList, processState: null, printer: this.selectedPrinter,
      machineType: this.carrierStateDetails.machineType};

    this.containerSplitService.generateBarcodes(this.splitRequest).subscribe(
      results => {
        this.outputBarcodeList = results.data;
        this.getOutputBarcodes();
        console.log("output containers are:" + results.data);
        this._messagesService.addSuccessMessage({ severity: 'success', summary: 'barcodes generated', detail: 'Container Barcodes are generated.. ' });
        this.blocked = false
      },
      error => {
        console.log(error);
        let errMessage = this.errorHandler.handleError(error);
        this._messagesService.addErrorMessage(errMessage);
        this.blocked = false
    });
  }

  /**
   * @function
   * @description: This function is to validate, start and generate controle file.
   */
  startAndGenerateControleFile() {

    this.blocked = true
    this.isReadyForSubmit = false;
    this.isInputDataValid = this.validateInputData();
    let containerProcessComplete: boolean
    let hamiltonFileGenerationComplete: boolean

    if (this.isInputDataValid) {
      this.splitRequest.processState = ProcessState.STARTED

      //call method to start splitting
      this.containerSplitService.processSplitContainers(this.splitRequest).subscribe(
        results => {
          if(results.data)
          {
          this._messagesService.addSuccessMessage({ severity: 'success', summary: 'splitting started..', detail: 'Splitting Process is started.. ' });
          }
          containerProcessComplete = true
          this.blocked = !(containerProcessComplete && hamiltonFileGenerationComplete)
        },
        error => {
          console.log(error);
          let errMessage = this.errorHandler.handleError(error);
          this._messagesService.addErrorMessage(errMessage);
          this.blocked = false
        });

      //call method to generate Hamilton file
      this.containerSplitService.generateHamiltonFile(this.splitRequest).subscribe(
        blob => {
          var link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = this.containerBarcode + '.xls';
          link.click();
          this._messagesService.addSuccessMessage({ severity: 'success', summary: 'Downloaded successful', detail: 'Downloaded Hamilton Control file' });
          hamiltonFileGenerationComplete = true
          this.blocked = !(containerProcessComplete && hamiltonFileGenerationComplete)
        },
        error => {
          console.log(error);
          let errMessage = this.errorHandler.handleError(error);
          this._messagesService.addErrorMessage(errMessage);
          this.blocked = false
        });

        
    }

  }

  /**
   * @function
   * @description: This function is to cancel spitting.
   */
  cancelSplitting() {
    
    this.blocked = true
    this.isInputDataValid = this.validateInputData();
    if (this.isInputDataValid) {
      this.splitRequest.processState = ProcessState.CANCELED

      //call method to start splitting
      this.containerSplitService.processSplitContainers(this.splitRequest).subscribe(
        results => {
          if(results.data)
          {
          this._messagesService.addSuccessMessage({ severity: 'warning', summary: 'splitting cancelled..', detail: 'Splitting Process is cancelled.. ' });
          }
          this.blocked = false
        },
        error => {
          console.log(error);
          let errMessage = this.errorHandler.handleError(error);
          this._messagesService.addErrorMessage(errMessage);
          this.blocked = false
        });
      this.isReadyForSubmit = true;
    }
  }

  /**
   * @function
   * @description: This function is to validate and complete splitting.
   */
  completeSplitting() {

    this.blocked = true
    this.isInputDataValid = this.validateInputData();
    if (this.isInputDataValid) {
      this.splitRequest.processState = ProcessState.COMPLETED

      //call method to start splitting
      this.containerSplitService.processSplitContainers(this.splitRequest).subscribe(
        results => {
          if(results.data)
          {
          this._messagesService.addSuccessMessage({ severity: 'success', summary: 'splitting completed..', detail: 'Splitting Process is Completed.. ' });
          }
          this.blocked = false
        },
        error => {
          console.log(error);
          let errMessage = this.errorHandler.handleError(error);
          this._messagesService.addErrorMessage(errMessage);
          this.blocked = false
        });
      this.isReadyForSubmit = true;
    }
  }

  //fetch the barcodes for output fields
  getOutputBarcodes() {
    if (this.outputBarcodeList) {
      //set the output barcodes to split request
      this.splitRequest.targetBarcodes = this.outputBarcodeList;
      for (let barcode of this.outputBarcodeList) {
        //if barcode name contains A1
        if (barcode.search("A1") != -1) {
          this.barcodeA1 = barcode;
        }
        //if barcode name contains A2
        else if (barcode.search("A2") != -1) {
          this.barcodeA2 = barcode;
        }
        //if barcode name contains B1
        else if (barcode.search("B1") != -1) {
          this.barcodeB1 = barcode;
        }
        //if barcode name contains B2
        else if (barcode.search("B2") != -1) {
          this.barcodeB2 = barcode;
        }
      }
    }
  }

  validateInputData(): boolean {
    this.isInputDataValid = true;
    if (!this.splitDoneBy) {
      this._messagesService.addErrorMessage(this.invalidPUIDError);
      this.isInputDataValid = false;
    }
    else if (!this.outputBarcodeList) {
      this._messagesService.addErrorMessage(this.outputBarcodesNotAvailableError);
      this.isInputDataValid = false;
    }
    return this.isInputDataValid;
  }

  /**
   * Below are the error messages displayed if validation failure.
   */
  invalidPUIDError = {
    severity: 'error',
    summary: 'PUID Is Not Valid',
    detail: "Please enter PUID"
  }

  machineNotFoundError = {
    severity: 'error',
    summary: 'Invalid Machine Barcode',
    detail: "Machine does not exists in the System"
  }

  machineNotActiveError = {
    severity: 'error',
    summary: 'Machine Is Not Active',
    detail: "Selected machine is not active"
  }

  machineTypeError = {
    severity: 'error',
    summary: 'Invalid Machine Type',
    detail: "Selected machine is not Hamilton"
  }

  containerNotFoundError = {
    severity: 'error',
    summary: 'Invalid Container Barcode',
    detail: "Container does not exists in the System"
  }

  masterDataNotFoundError = {
    severity: 'error',
    summary: 'Master Data Not Exists',
    detail: "No machines found in master data"
  }

  printerNotSelectedError = {
    severity: 'error',
    summary: 'Invalid Printer',
    detail: "Printer not selected"
  }

  outputBarcodesNotAvailableError = {
    severity: 'error',
    summary: 'No generated Barcodes',
    detail: "Target Barcodes are not scanned"
  }

  //reset Output Brcode values
  resetReadOnlyFields() {
    this.barcodeA1 = "";
    this.barcodeA2 = "";
    this.barcodeB1 = "";
    this.barcodeB2 = "";
    this.outputBarcodeList = null;
  }

  /**
  * @function
  * @description: This method is to TBD
  */
  onSubmit(value: string) {
    if (this.isReadyForSubmit) {
      this.stepSplitForm.reset();
      this.resetReadOnlyFields();
      document.getElementById('splitDoneBy').focus();
      this.isReadyForSubmit = false;
    }
  }

  /**
  * @function
  * @description: This method is to avoid default submit
  */
  disableDefaultSubmit(event) {
    event.preventDefault();
    event.target.blur();
  }

  /**
   * @function
   * @description: This method is to get the focus to Carrier Barcode Field
   */
  focusContainerbarcode() {
    document.getElementById('containerField').focus();
  }
  /**
   * @function
   * @description: This method is to get the focus to Machine Barcode Field
   */
  focusMachinebarcode() {
    document.getElementById('machineField').focus();
  }

}
